import { useState } from 'react';
import { fmtPhone, teamsCallUrl } from '@lib/care-model';
import { placeCall, type CallTarget } from '@lib/call-guard';

/**
 * PhoneActions - a phone number with its operator actions: place the call in Teams (only after the stop
 * check bound to the case says yes, lib/call-guard.ts; Teams opens pre-staged to dial, nothing dials
 * automatically), text the number, and copy it.
 * Ergonomics roll-up 2026-07-11: the out-calling workflow was "read the number, retype it into
 * Teams" - both steps this component deletes are transcription-error surface.
 *
 * TEXT IS OPT-IN PER CALL SITE (`onText`, William 2026-08-11). It appears only where the caller can
 * actually honour it, which today is the drawer header, where the number shown IS the number the
 * conversation's compose box sends to. Offering it beside a member's second roster number would
 * promise a destination the composer does not use, so those sites pass no handler and show no
 * button.
 *
 * Renders inline; stopPropagation on every action so it can sit inside a clickable table row or a
 * drawer header without opening/closing anything.
 */
export function PhoneActions({
  phone,
  compact = false,
  blocked = false,
  checkBeforeCall,
  onText,
}: {
  phone: string;
  compact?: boolean;
  /**
   * The member behind this number is CURRENTLY marked do_not_solicit (derived live from 26fobt by
   * the list projection, never stamped). The call and text actions render disabled with the reason;
   * copy stays available (the number itself is not the harm, the outbound contact is). rev22 banner
   * follow-up, built 2026-07-22 after the false-optout harms made it urgent.
   */
  blocked?: boolean;
  /**
   * W9 (00 Main's RULED 01.59.17 AM, 11 Oct 2026): the Call button asks the server whether the number is stopped,
   * under any plan, before Teams opens (lib/call-guard.ts), and opens it only on a plain yes; a no shows the one
   * sentence beside the number.
   * BOUND TO THE CASE (T-1010-99-CALLBIND, 1:46 PM 11 Oct 2026): the site names the case this number is on and
   * which of its numbers it is; the server reads the number from the case, never from the press, and on a yes
   * Teams dials the number the server checked.
   * EVERY MEMBER CALL PASSES THE STOP CHECK (the office, 17 Oct 2026, interim line 5): there is no plain Teams link
   * any more. A site that names no case draws NO Call button at all (copy and Text stay), so no number can be rung
   * from this console without the check.
   */
  checkBeforeCall?: CallTarget;
  /** Jump to the conversation's compose box for this number. Omit to hide the Text button. */
  onText?: () => void;
}): JSX.Element | null {
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [callSaid, setCallSaid] = useState('');
  const display = fmtPhone(phone);
  const call = teamsCallUrl(phone);
  if (!display) return null;

  /**
   * QA-1655 F10 (William, 17 Oct 2026): AFTER A REFUSAL THE NUMBER IS NOT COPIED. The Copy button stayed live beside a
   * refused Call, so the number could be pasted into Teams by hand and the stop check walked round. Once the server
   * says no, Copy is disabled and says why (the refusal stays shown beside the number); the next press of Call asks
   * again and, on a yes, Copy comes back.
   */
  const copyOff = callSaid !== '';

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (copyOff) return;
    try {
      await navigator.clipboard.writeText(display);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (permissions/http): the number is still on screen to read */
    }
  };

  const onCall = async (): Promise<void> => {
    if (!checkBeforeCall) return;
    setChecking(true);
    setCallSaid('');
    try {
      const r = await placeCall(phone, checkBeforeCall);
      if (!r.placed) setCallSaid(r.said);
    } finally {
      setChecking(false);
    }
  };

  return (
    <span className="phone-actions" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
      <span>{display}</span>
      {call && blocked && (
        <button
          type="button"
          className="btn btn-ghost btn-sm phone-act"
          disabled
          title="This member is marked do-not-solicit. Do not place this call."
          aria-label={`Calling ${display} is blocked: the member is marked do-not-solicit`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          ☎{compact ? '' : ' Call'} <span aria-hidden="true">🚫</span>
        </button>
      )}
      {call && !blocked && checkBeforeCall && (
        <button
          type="button"
          className="btn btn-ghost btn-sm phone-act"
          disabled={checking}
          title={`Call ${display} in Microsoft Teams`}
          aria-label={`Call ${display} in Microsoft Teams`}
          data-testid="guarded-call"
          onClick={(e) => { e.stopPropagation(); void onCall(); }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {'\u260E'}{compact ? '' : ' Call'}
        </button>
      )}
      {callSaid && <span className="micro" role="status" data-testid="call-refused">{callSaid}</span>}
      {onText && (
        <button
          type="button"
          className="btn btn-ghost btn-sm phone-act"
          disabled={blocked}
          title={blocked
            ? 'This member is marked do-not-solicit. Do not text this number.'
            : `Text ${display}`}
          aria-label={blocked
            ? `Texting ${display} is blocked: the member is marked do-not-solicit`
            : `Text ${display}`}
          onClick={(e) => { e.stopPropagation(); if (!blocked) onText(); }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          ✉{compact ? '' : ' Text'}{blocked && <> <span aria-hidden="true">🚫</span></>}
        </button>
      )}
      <button
        type="button"
        className="btn btn-ghost btn-sm phone-act"
        disabled={copyOff}
        data-testid="copy-phone"
        title={copyOff ? `Copy is off: ${callSaid}` : copied ? 'Copied' : `Copy ${display}`}
        aria-label={copyOff ? `Copy ${display} is off: ${callSaid}` : `Copy ${display}`}
        onClick={(e) => void onCopy(e)}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {copied ? '✓' : '⧉'}
      </button>
    </span>
  );
}
